/* eslint-disable camelcase */
import { differenceInYears } from 'date-fns';
import {
  FaRegCalendarAlt, FaMale, FaHome, FaShieldAlt,
} from 'react-icons/fa';
import { get } from 'lodash';

export const formatQuarter = (quarter) => {
  const [year, quarterNumber] = quarter.split('_');
  if (quarterNumber) return `${year} Q${quarterNumber}`;
  return `${year}`;
};

const READABLE_STRINGS = {
  rb_uo: 'Unoccupied',
  rb_ro: 'Renter Occupied',
  rb_oo: 'Owner Occupied',
  prc_sq_ft: 'Average Sale Price Per Square Foot',
  sales_count: 'Sales Count',
  transactions_count: 'Sales Count',
  price_per_sq_ft: 'Average Sale Price Per Square Foot',
};

export const makeStringReadable = (str) => READABLE_STRINGS[str] || str;

export const getSelectedParcelsTitle = (reportContents, prop = {}) => (reportContents.layer
  ? get(reportContents, 'layer.label', 'Selected Parcels')
  : `Parcels within ${prop.radius ? prop.radius : 500}ft`);

export const parseData = (graph) => Object.values(
  graph.data.reduce((acc, dataPoint) => {
    if (!dataPoint.group_by) return acc;
    const group = formatQuarter(dataPoint.group_by);
    acc[group] = acc[group] || { group_by: group };
    acc[group][makeStringReadable(dataPoint.column)] = dataPoint.value;
    return acc;
  }, {}),
);

export const parseKeys = (graph) => Object.keys(
  graph.data.reduce((acc, { column }) => {
    acc[makeStringReadable(column)] = true;
    return acc;
  }, {}),
).sort();

export const parseLineData = (
  graph = { data: [] },
  comparison = { data: [] },
  useRatio = true,
) => {
  // calculate values by percentages for each record
  // split data by property : Unoccupied vs Occupied
  let accBase = {};
  const columns = {};
  if (comparison.data.length > graph.data.length) {
    accBase = comparison.data.reduce((acc, dataPoint) => {
      if (!dataPoint.group_by) return acc;
      const group = formatQuarter(dataPoint.group_by);
      const column = makeStringReadable(dataPoint.column);
      acc[group] = acc[group] || {};
      acc[group][column] = 0;
      columns[column] = 0;
      return acc;
    }, {});
  }

  const valuesByQuarter = graph.data.reduce((acc, dataPoint) => {
    if (!dataPoint.group_by) return acc;
    const group = formatQuarter(dataPoint.group_by);
    const column = makeStringReadable(dataPoint.column);
    acc[group] = acc[group] || {};
    acc[group][column] = dataPoint.value;
    columns[column] = null;
    return acc;
  }, accBase);

  if (columns.null != null) delete columns.null;

  const valuesByQuarterParsed = Object.keys(valuesByQuarter).reduce(
    (totalsByQuarter, quarterLabel) => {
      const quarter = valuesByQuarter[quarterLabel];
      const total = Object.values(quarter).reduce((acc, value) => {
        if (acc == null && value != null) return value;
        if (value) {
          return acc + value;
        }
        return acc;
      }, null);

      // eslint-disable-next-line no-param-reassign
      totalsByQuarter[quarterLabel] = Object.keys(quarter).reduce(
        (acc, key) => {
          if (key === 'null') return acc;
          if (total === 0) {
            acc[key] = 0;
          } else if (useRatio) {
            acc[key] = Math.round((quarter[key] / total) * 10000) / 100;
          } else {
            acc[key] = quarter[key];
          }

          return acc;
        },
        {},
      );
      return totalsByQuarter;
    },
    {},
  );

  const results = Object.keys(valuesByQuarterParsed).reduce(
    (acc, quarterLabel) => {
      const quarter = {
        ...columns,
        ...valuesByQuarterParsed[quarterLabel],
      };
      Object.keys(quarter).forEach((value) => {
        acc[value] = acc[value] || [];
        acc[value].push({
          x: quarterLabel,
          y: quarter[value],
        });
      });
      return acc;
    },
    {},
  );
  return results;
};

export const prepareParcelForRendering = (parcel = {}) => {
  const {
    parcel_id,
    zoning,
    owner_name,
    lst_sle_dt,
    rb_flr_ar_num,
    cib_flr_ar_num,
    rb_nbed = 0,
    bath_num = 0,
    rb_yr_blt,
    taxStatus,
    occStatus,
    own_prop_ct,
    esttcv,
  } = parcel;

  return [
    {
      label: 'Parcel Id',
      value: parcel_id,
    },
    {
      label: 'Zoning',
      value: zoning,
    },
    {
      Icon: FaMale,
      label: 'Owner Name',
      value: owner_name,
    },
    {
      Icon: FaRegCalendarAlt,
      label: 'Owner Since',
      value: lst_sle_dt,
    },
    {
      label: 'Other properties owned across the city',
      value: own_prop_ct,
    },
    {
      Icon: FaHome,
      label: 'Square Feet',
      value: rb_flr_ar_num || cib_flr_ar_num,
    },
    {
      label: 'Configuration',
      value: `${rb_nbed} Bed/${bath_num} Bath`,
    },
    {
      label: 'Age of Property',
      value: differenceInYears(new Date(), new Date(`${rb_yr_blt}-01-01`)),
    },
    {
      label: 'Tax Status',
      value: taxStatus,
    },
    {
      label: 'Occupancy',
      value: occStatus,
    },
    {
      label: 'Est. True Cash Value',
      value: esttcv,
    },
  ];
};

export const prepareLicenseForRendering = ({
  open_prmts,
  rental,
  owner_name,
  pre,
} = {}) => [
    {
      label: 'Rental Registration',
      value: rental,
    },
    {
      label: 'PRE',
      value: pre,
    },
    {
      label: 'LARA License (where applicable)',
      value: {
        label: owner_name ? `Copy to clipboard and open: ${owner_name}` : '',
        onClick: () => {
          const copyText = document.createElement('input');
          copyText.value = owner_name;

          document.body.appendChild(copyText);
          copyText.select();
          document.execCommand('copy');

          window.open(
            'https://cofs.lara.state.mi.us/corpweb/CorpSearch/CorpSearch.aspx',
          );
          copyText.remove();
        },
      },
    },
    {
      label: 'Open permits count',
      value: open_prmts,
    },
  ];

export const prepareViolationsForRendering = ({
  violations,
  open_vio,
  oth_prop_vios,
  incdnt_cnt,
} = {}) => [
    {
      Icon: FaShieldAlt,
      label: 'Crime and recent calls for service - ',
      link: {
        label: 'Crime Mapping',
        href: 'http://gis.co.jackson.mi.us/CrimeMapping/index.html',
      },
      value: incdnt_cnt || 0,
    },
    {
      label: 'Open violations/enforcement count',
      value: open_vio,
    },
    {
      label:
        'Violations since last change of ownership at this property or in the past two years',
      value: violations,
    },
    {
      label:
        'Violations at other properties owned by owner in past 2 years or since ownership',
      value: oth_prop_vios,
    },
  ];

export const sortByLabels = (a, b) => {
  if (a.label > b.label) return -1;
  if (b.label > a.label) return 1;
  return 0;
};
